import React, { useState } from 'react'
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from '@mui/icons-material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm, Controller } from 'react-hook-form'
import { toast } from 'react-toastify'

import api from '../../services/api'
import { extractErrorMessage } from '../../utils/errorHandler'
import RequiredFieldLabel from '../../components/Common/RequiredFieldLabel'

interface Department {
  department_id: string
  department_name: string
  department_type: 'tech' | 'sales' | 'admin'
  parent_department_id: string | null
  manager_id: string | null
  manager_name?: string
  parent_department_name?: string
  employee_count: number
  is_active: boolean
  created_at: string
  updated_at: string
}

interface DepartmentFormData {
  department_id: string
  department_name: string
  department_type: 'tech' | 'sales' | 'admin'
  parent_department_id: string
  manager_id: string
}

const DEPARTMENT_TYPES = {
  tech: '技術部門',
  sales: '営業部門',
  admin: '管理部門',
}

const DepartmentManagementPage: React.FC = () => {
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null)
  const [deletingDepartment, setDeletingDepartment] = useState<Department | null>(null)
  const [showInactive, setShowInactive] = useState(false)

  // 部門タイプ変更用
  const [typeChangeDialogOpen, setTypeChangeDialogOpen] = useState(false)
  const [typeChangeReason, setTypeChangeReason] = useState('')
  const [pendingFormData, setPendingFormData] = useState<DepartmentFormData | null>(null)

  // 編集前の値を保持（変更検知用）
  const [originalManagerId, setOriginalManagerId] = useState<string | null>(null)
  const [originalDepartmentType, setOriginalDepartmentType] = useState<'tech' | 'sales' | 'admin' | null>(null)
  const [originalDepartmentName, setOriginalDepartmentName] = useState<string | null>(null)
  const [originalParentDepartmentId, setOriginalParentDepartmentId] = useState<string | null>(null)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm<DepartmentFormData>({
    defaultValues: {
      department_id: '',
      department_name: '',
      department_type: 'tech',
      parent_department_id: '',
      manager_id: '',
    },
  })

  // Fetch departments
  const { data: departmentsData, isLoading, refetch } = useQuery({
    queryKey: ['departments', showInactive],
    queryFn: async () => {
      const url = showInactive
        ? '/departments?include_inactive=true'
        : '/departments'
      const response = await api.get<{ success: boolean; data: Department[] }>(url)
      return response.data.data
    },
  })

  // Fetch employees for manager dropdown (using simple endpoint)
  const { data: employeesData } = useQuery({
    queryKey: ['employees-for-manager'],
    queryFn: async () => {
      const response = await api.get<{
        success: boolean;
        data: Array<{ employee_id: string; name: string }>;
      }>('/employees/simple')
      return response.data.data
    },
  })

  // Create department mutation
  const createDepartmentMutation = useMutation({
    mutationFn: async (data: DepartmentFormData) => {
      return await api.post('/departments', {
        ...data,
        parent_department_id: data.parent_department_id || null,
        manager_id: data.manager_id || null,
      })
    },
    onSuccess: () => {
      toast.success('部門を作成しました')
      queryClient.invalidateQueries({ queryKey: ['departments'] })
      setDialogOpen(false)
      reset()
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, '部門の作成に失敗しました'))
    },
  })

  // Update department mutation
  const updateDepartmentMutation = useMutation({
    mutationFn: async (data: DepartmentFormData) => {
      const { department_id, ...updateData } = data
      return await api.put(`/departments/${department_id}`, {
        ...updateData,
        parent_department_id: updateData.parent_department_id || null,
        manager_id: updateData.manager_id || null,
      })
    },
    onSuccess: () => {
      toast.success('部門情報を更新しました')
      queryClient.invalidateQueries({ queryKey: ['departments'] })
      setDialogOpen(false)
      setEditingDepartment(null)
      reset()
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, '部門の更新に失敗しました'))
    },
  })

  // 部門名・親部門のみを更新する（部門長・部門種別の変更と併用するため）。
  // 部門長は承認者の自動切替、部門種別は変更履歴の記録を伴うので専用APIに任せ、ここでは送らない。
  const updateDepartmentBasicMutation = useMutation({
    mutationFn: async (data: DepartmentFormData) => {
      return await api.put(`/departments/${data.department_id}`, {
        department_name: data.department_name,
        parent_department_id: data.parent_department_id || null,
      })
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, '部門情報の更新に失敗しました'))
    },
  })

  // Deactivate department mutation
  const deactivateDepartmentMutation = useMutation({
    mutationFn: async (departmentId: string) => {
      return await api.patch(`/departments/${departmentId}/deactivate`)
    },
    onSuccess: () => {
      toast.success('部門を無効化しました')
      queryClient.invalidateQueries({ queryKey: ['departments'] })
      setDeleteDialogOpen(false)
      setDeletingDepartment(null)
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, '部門の無効化に失敗しました'))
    },
  })

  // Reactivate department mutation
  const reactivateDepartmentMutation = useMutation({
    mutationFn: async (departmentId: string) => {
      return await api.patch(`/departments/${departmentId}/reactivate`)
    },
    onSuccess: () => {
      toast.success('部門を有効化しました')
      queryClient.invalidateQueries({ queryKey: ['departments'] })
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, '部門の有効化に失敗しました'))
    },
  })

  // Update department manager mutation
  const updateManagerMutation = useMutation({
    mutationFn: async ({ departmentId, newManagerId }: { departmentId: string; newManagerId: string | null }) => {
      return await api.put(`/departments/${departmentId}/manager`, {
        new_manager_id: newManagerId
      })
    },
    onSuccess: () => {
      toast.success('部門長を変更しました。承認者は自動的に新部門長に切り替わります。')
      queryClient.invalidateQueries({ queryKey: ['departments'] })
      setDialogOpen(false)
      setEditingDepartment(null)
      reset()
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, '部門長の変更に失敗しました'))
    },
  })

  // Update department type mutation
  const updateTypeMutation = useMutation({
    mutationFn: async ({ departmentId, newType, reason }: { departmentId: string; newType: 'tech' | 'sales' | 'admin'; reason: string }) => {
      return await api.put(`/departments/${departmentId}/type`, {
        new_type: newType,
        reason
      })
    },
    onSuccess: () => {
      toast.success('部門タイプを変更しました')
      queryClient.invalidateQueries({ queryKey: ['departments'] })
      setDialogOpen(false)
      setEditingDepartment(null)
      setTypeChangeDialogOpen(false)
      setTypeChangeReason('')
      setPendingFormData(null)
      reset()
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, '部門タイプの変更に失敗しました'))
    },
  })

  const handleAddDepartment = () => {
    setEditingDepartment(null)
    reset({
      department_id: '',
      department_name: '',
      department_type: 'tech',
      parent_department_id: '',
      manager_id: '',
    })
    setDialogOpen(true)
  }

  const handleEditDepartment = (department: Department) => {
    setEditingDepartment(department)
    // 変更検知用に元の値を保存
    setOriginalManagerId(department.manager_id || null)
    setOriginalDepartmentType(department.department_type)
    setOriginalDepartmentName(department.department_name)
    setOriginalParentDepartmentId(department.parent_department_id || null)
    reset({
      department_id: department.department_id,
      department_name: department.department_name,
      department_type: department.department_type,
      parent_department_id: department.parent_department_id || '',
      manager_id: department.manager_id || '',
    })
    setDialogOpen(true)
  }

  const handleDeleteClick = (department: Department) => {
    setDeletingDepartment(department)
    setDeleteDialogOpen(true)
  }

  const handleToggleActive = async (department: Department) => {
    if (department.is_active) {
      handleDeleteClick(department)
    } else {
      await reactivateDepartmentMutation.mutateAsync(department.department_id)
    }
  }

  // 部門名・親部門が変更されたか（部門長・部門種別の専用APIでは更新されない項目）
  const isBasicFieldChanged = (data: DepartmentFormData) =>
    data.department_name !== (originalDepartmentName ?? '') ||
    (data.parent_department_id || null) !== originalParentDepartmentId

  const onSubmit = async (data: DepartmentFormData) => {
    // 新規作成の場合は通常のAPI
    if (!editingDepartment) {
      await createDepartmentMutation.mutateAsync(data)
      return
    }

    // 編集の場合: 変更を検知して適切なAPIに振り分け
    const managerChanged = (data.manager_id || null) !== originalManagerId
    const typeChanged = data.department_type !== originalDepartmentType

    // 部門長のみ変更された場合
    if (managerChanged && !typeChanged) {
      // 部門名・親部門も同時に変更されている場合は、それらも保存する
      if (isBasicFieldChanged(data)) {
        await updateDepartmentBasicMutation.mutateAsync(data)
      }
      await updateManagerMutation.mutateAsync({
        departmentId: data.department_id,
        newManagerId: data.manager_id || null
      })
      return
    }

    // 部門タイプが変更された場合（理由入力が必要）
    if (typeChanged) {
      // 理由入力ダイアログを表示
      setPendingFormData(data)
      setTypeChangeDialogOpen(true)
      return
    }

    // その他のフィールドのみ変更（部門名、親部門など）
    await updateDepartmentMutation.mutateAsync(data)
  }

  // 部門タイプ変更の確定処理
  const handleConfirmTypeChange = async () => {
    if (!pendingFormData || !typeChangeReason.trim()) {
      toast.error('変更理由を入力してください')
      return
    }

    // 部門名・親部門も同時に変更されている場合は、それらも保存する
    if (isBasicFieldChanged(pendingFormData)) {
      await updateDepartmentBasicMutation.mutateAsync(pendingFormData)
    }

    // 部門長も同時に変更されている場合は、先に部門長を変更
    const managerChanged = (pendingFormData.manager_id || null) !== originalManagerId
    if (managerChanged) {
      await updateManagerMutation.mutateAsync({
        departmentId: pendingFormData.department_id,
        newManagerId: pendingFormData.manager_id || null
      })
    }

    // 部門タイプを変更
    await updateTypeMutation.mutateAsync({
      departmentId: pendingFormData.department_id,
      newType: pendingFormData.department_type,
      reason: typeChangeReason
    })
  }

  const departments = departmentsData || []
  const activeDepartments = departments.filter((d) => d.is_active)

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BusinessIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            部門管理
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                color="primary"
              />
            }
            label="無効な部門を表示"
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
          >
            更新
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddDepartment}
          >
            新規
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Typography>読み込み中...</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>部門ID</TableCell>
                <TableCell>部門名</TableCell>
                <TableCell>種別</TableCell>
                <TableCell>親部門</TableCell>
                <TableCell>部門長</TableCell>
                <TableCell align="center">所属人数</TableCell>
                <TableCell align="center">状態</TableCell>
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {departments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    部門データがありません
                  </TableCell>
                </TableRow>
              ) : (
                departments.map((department) => (
                  <TableRow
                    key={department.department_id}
                    sx={{ opacity: department.is_active ? 1 : 0.6 }}
                  >
                    <TableCell>{department.department_id}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {department.department_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={DEPARTMENT_TYPES[department.department_type]}
                        size="small"
                        color={
                          department.department_type === 'tech'
                            ? 'primary'
                            : department.department_type === 'sales'
                            ? 'secondary'
                            : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>{department.parent_department_name || '-'}</TableCell>
                    <TableCell>{department.manager_name || '-'}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={department.employee_count}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {department.is_active ? (
                        <Chip
                          icon={<ActiveIcon />}
                          label="有効"
                          size="small"
                          color="success"
                        />
                      ) : (
                        <Chip
                          icon={<InactiveIcon />}
                          label="無効"
                          size="small"
                          color="default"
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="編集">
                        <IconButton
                          size="small"
                          onClick={() => handleEditDepartment(department)}
                          disabled={!department.is_active}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={department.is_active ? '無効化' : '有効化'}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleActive(department)}
                          color={department.is_active ? 'error' : 'success'}
                        >
                          {department.is_active ? <DeleteIcon /> : <ActiveIcon />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingDepartment ? '部門編集' : '新規部門追加'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Controller
                name="department_id"
                control={control}
                rules={{ required: '部門IDは必須です' }}
                render={({ field }) => (
                  <Box>
                    <RequiredFieldLabel label="部門ID" />
                    <TextField
                      {...field}
                      fullWidth
                      error={!!errors.department_id}
                      helperText={errors.department_id?.message}
                      disabled={!!editingDepartment}
                      placeholder="例: TECH_DEV"
                    />
                  </Box>
                )}
              />

              <Controller
                name="department_name"
                control={control}
                rules={{ required: '部門名は必須です' }}
                render={({ field }) => (
                  <Box>
                    <RequiredFieldLabel label="部門名" />
                    <TextField
                      {...field}
                      fullWidth
                      error={!!errors.department_name}
                      helperText={errors.department_name?.message}
                      placeholder="例: 技術開発部"
                    />
                  </Box>
                )}
              />

              <Controller
                name="department_type"
                control={control}
                rules={{ required: '部門種別は必須です' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.department_type}>
                    <RequiredFieldLabel label="部門種別" />
                    <Select {...field}>
                      <MenuItem value="tech">技術部門</MenuItem>
                      <MenuItem value="sales">営業部門</MenuItem>
                      <MenuItem value="admin">管理部門</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="parent_department_id"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>親部門（オプション）</InputLabel>
                    <Select {...field} label="親部門（オプション）">
                      <MenuItem value="">なし</MenuItem>
                      {activeDepartments
                        .filter((d) => d.department_id !== watch('department_id'))
                        .map((dept) => (
                          <MenuItem key={dept.department_id} value={dept.department_id}>
                            {dept.department_name} ({dept.department_id})
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="manager_id"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>部門長（オプション）</InputLabel>
                    <Select {...field} label="部門長（オプション）">
                      <MenuItem value="">未設定</MenuItem>
                      {employeesData?.map((emp: any) => (
                        <MenuItem key={emp.employee_id} value={emp.employee_id}>
                          {emp.name} ({emp.employee_id})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>キャンセル</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createDepartmentMutation.isPending || updateDepartmentMutation.isPending}
            >
              {editingDepartment ? '更新' : '作成'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>部門の無効化</DialogTitle>
        <DialogContent>
          {deletingDepartment && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>{deletingDepartment.department_name}</strong> を無効化しますか？
              </Typography>
              <Typography variant="caption" color="text.secondary">
                この部門に社員が所属している場合、または子部門がある場合は無効化できません。
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (deletingDepartment) {
                deactivateDepartmentMutation.mutate(deletingDepartment.department_id)
              }
            }}
            disabled={deactivateDepartmentMutation.isPending}
          >
            無効化
          </Button>
        </DialogActions>
      </Dialog>

      {/* Type Change Reason Dialog */}
      <Dialog
        open={typeChangeDialogOpen}
        onClose={() => {
          setTypeChangeDialogOpen(false)
          setTypeChangeReason('')
          setPendingFormData(null)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>部門タイプ変更</DialogTitle>
        <DialogContent>
          {pendingFormData && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom>
                  部門タイプを <strong>{DEPARTMENT_TYPES[originalDepartmentType!]}</strong> から{' '}
                  <strong>{DEPARTMENT_TYPES[pendingFormData.department_type]}</strong> に変更します。
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  この変更は重要なイベントとしてログに記録されます。変更理由を必ず入力してください。
                </Typography>
              </Alert>

              <Box>
                <RequiredFieldLabel label="変更理由" />
                <TextField
                  fullWidth
                  value={typeChangeReason}
                  onChange={(e) => setTypeChangeReason(e.target.value)}
                  multiline
                  rows={3}
                  placeholder="例: 組織再編により営業部門から技術部門に移管"
                  helperText="部門タイプを変更する理由を詳しく記入してください"
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setTypeChangeDialogOpen(false)
              setTypeChangeReason('')
              setPendingFormData(null)
            }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmTypeChange}
            disabled={updateTypeMutation.isPending || updateManagerMutation.isPending}
          >
            変更を確定
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default DepartmentManagementPage
